/**
 * Poll detail — full question, vote flow, results bars with %, my vote
 * highlighted. Ported from PollDetail in design/source/screens-poll-event.jsx.
 */
import { useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import {
  BottomBar, CardSkeletons, goBack, PollOptionRow, PollResultRow, ScopeBadge, SubTabs as _unused, // eslint-disable-line @typescript-eslint/no-unused-vars
} from '@/features/community/bits';
import { fetchPoll, pct, useFetch, votePoll, withVote } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Pill, Screen, TopBar } from '@/ui';

export default function PollDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useT();
  const { colors } = useTheme();
  const { data: poll, loading, error, refetch, mutate } = useFetch(() => fetchPoll(String(id)), [id]);

  const [choice, setChoice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [voteErr, setVoteErr] = useState<string | null>(null);

  const voted = !!poll && (poll.myOptionId != null || poll.status === 'closed');
  const sum = poll ? poll.options.reduce((s, o) => s + o.votes, 0) : 0;

  const submit = async () => {
    if (!poll || choice == null || busy) return;
    const prev = poll;
    const idx = poll.options.findIndex((o) => o.id === choice);
    setBusy(true);
    setVoteErr(null);
    mutate(() => withVote(prev, choice));
    try {
      await votePoll(prev.id, choice, idx);
    } catch {
      mutate(() => prev);
      setVoteErr(t('poll_vote_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('poll_detail_title')} onBack={goBack} />
      {loading ? (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <CardSkeletons count={2} />
        </Screen>
      ) : error || !poll ? (
        <Screen padded>
          <EmptyState
            title={t('poll_unavailable_title')}
            body={t('poll_unavailable_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        </Screen>
      ) : (
        <>
          <Screen padded style={{ paddingTop: space.s4 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
              <ScopeBadge scope={poll.scope} />
              {poll.anonymous ? <Pill status="pending" label={t('ct_anonymous')} /> : null}
            </View>
            <AppText variant="headingLg" style={{ marginTop: space.s3 }}>{poll.question}</AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
              {t('poll_posted_by', { name: poll.postedBy })} · {poll.deadline}
            </AppText>

            <View style={{ marginTop: space.s6, gap: space.s2 }}>
              {voted
                ? poll.options.map((o) => (
                    <PollResultRow key={o.id} label={o.label} pct={pct(o.votes, sum)} mine={poll.myOptionId === o.id} />
                  ))
                : poll.options.map((o) => (
                    <PollOptionRow key={o.id} label={o.label} selected={choice === o.id} onPress={() => setChoice(o.id)} />
                  ))}
            </View>

            {voted ? (
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s4 }}>
                {t('poll_total_votes', { n: sum })}{poll.anonymous ? ` · ${t('poll_anon_note')}` : ''}
              </AppText>
            ) : null}
            {voteErr ? (
              <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>{voteErr}</AppText>
            ) : null}
          </Screen>

          {!voted ? (
            <BottomBar>
              <Button title={t('poll_submit_vote')} size="lg" disabled={choice == null} loading={busy} onPress={() => void submit()} />
            </BottomBar>
          ) : null}
        </>
      )}
    </View>
  );
}
